import { EyeOutlined } from "@ant-design/icons";
import { Button, Drawer, Dropdown, Form, Select, Space, Switch } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import ActionThreeDotIcon from "../../../assets/icons/ActionThreeDotIcon";
import AddIcon from "../../../assets/icons/AddIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import FilterIconThree from "../../../assets/icons/FilterIconThree";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SuccessModal from "../../../components/common/SuccessModal";
import CustomTable from "../../../components/ui/CustomTable";
import IconButton from "../../../components/ui/IconButton";
import Loader from "../../../components/ui/Loader";
import {
  useCreateSRMutation,
  useGetAllSRQuery,
  useGetSingleSRQuery,
  useUpdateSRMutation,
  useUpdateSRStatusMutation,
} from "../../../features/cashCollection/cashCollectionSetup/cashCollectionSetupApi";
import {
  useGetAllAreaQuery,
  useGetAllRouteQuery,
  useGetAllTerritoryQuery,
  useGetAllZoneQuery,
} from "../../../features/cashCollection/geoSetup/geoSetupApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import ConfirmModalLayout from "../../../layouts/ConfirmModalLayout";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { GetListRoutesSRDataType } from "../../../types/cashCollection/cashCollectionSetup";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import { addKeyInArray, formatNumber } from "../../../utils/utils";
import CashCollectionDetailsModal from "./CashCollectionDetailsModal";

export default function FieldOfficersDetails() {
  const backLink = `/admin/cash-field-officers`;
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const userName = searchParams.get("userName");
  const [form] = Form.useForm();
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<any>();

  const {
    open: openViewModal,
    handleOpen: handleViewOpen,
    handleClose: handleViewClose,
  } = useModal<undefined, "VIEW">();

  const {
    open: openConfirmModal,
    handleOpen: handleConfirmOpen,
    handleClose: handleConfirmClose,
  } = useModal<any, "CONFIRM">();

  const {
    open: openConfirmStatusModal,
    handleOpen: handleConfirmStatusOpen,
    handleClose: handleConfirmStatusClose,
  } = useModal<any, "CONFIRMSTATUS">();

  const {
    open: openSuccessModal,
    handleOpen: handleSuccessOpen,
    handleClose: handleSuccessClose,
  } = useModal<undefined, "SUCCESS">();

  const [open, setOpen] = useState(false);
  const [openOfficer, setOpenOfficer] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(
    filter?.zoneId ?? null
  );
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(
    filter?.areaId ?? null
  );
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<number | null>(
    filter?.territoryId ?? null
  );
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(
    filter?.routeId ?? null
  );
  const [selectedUserId, setSelectedUserId] = useState<number | null>(
    filter?.userId ?? null
  );

  // ---- Query -----
  const { user } = useAppSelector((state) => state.auth);

  const { isError, error, data, isLoading } = useGetAllSRQuery(
    {
      params: {
        ...filter,
        search: keyword,
        page,
        limit,
        companyId: user?.data?.company_id,
        userId: userId,
      },
    },
    {
      skip: !userId, // <-- correct place
    }
  );

  // Get Zone
  const {
    isError: getAllZoneIsError,
    error: getAllZoneError,
    data: getAllZoneData,
    isLoading: getAllZoneIsLoading,
    isFetching: getAllZoneIsFetching,
  } = useGetAllZoneQuery({
    params: {
      page: 1,
      limit: 1000000000000,
    },
  });

  // Get all Areas
  const {
    isError: getAllAreaIsError,
    error: getAllAreaError,
    data: getAllAreaData,
    isLoading: getAllAreaIsLoading,
    isFetching: getAllAreaIsFetching,
  } = useGetAllAreaQuery(
    {
      params: {
        page: 1,
        limit: 1000000000000,
        zoneId: selectedZoneId ? selectedZoneId : undefined,
      },
    },
    {
      skip: !selectedZoneId,
    }
  );

  // Get all Territories
  const {
    isError: getAllTerritoryIsError,
    error: getAllTerritoryError,
    data: getAllTerritoryData,
    isLoading: getAllTerritoryIsLoading,
    isFetching: getAllTerritoryIsFetching,
  } = useGetAllTerritoryQuery(
    {
      params: {
        page: 1,
        limit: 1000000000000,
        areaId: selectedAreaId ? selectedAreaId : undefined,
      },
    },
    {
      skip: !selectedAreaId,
    }
  );

  // Get all Routes
  const {
    isError: getAllRouteIsError,
    error: getAllRouteError,
    data: getAllRouteData,
    isLoading: getAllRouteIsLoading,
    isFetching: getAllRouteIsFetching,
  } = useGetAllRouteQuery(
    {
      params: {
        page: 1,
        limit: 1000000000000,
        territoryId: selectedTerritoryId ? selectedTerritoryId : undefined,
      },
    },
    {
      skip: !selectedTerritoryId,
    }
  );

  const {
    isError: getSingleSRIsError,
    error: getSingleSRError,
    isLoading: getSingleSRIsLoading,
    data: getSingleSRData,
    isFetching: getSingleSRIsFetching,
  } = useGetSingleSRQuery(selectedId, {
    skip: !selectedId,
  });

  // ------ Mutation --------

  const [
    createSR,
    {
      isError: createSRIsError,
      error: createSRError,
      isSuccess: createSRIsSuccess,
      isLoading: createSRIsLoading,
    },
  ] = useCreateSRMutation();

  const [
    updateSR,
    {
      isError: updateSRIsError,
      error: updateSRError,
      isLoading: updateSRIsLoading,
      isSuccess: updateSRIsSuccess,
    },
  ] = useUpdateSRMutation();

  const [
    updateSRStatus,
    {
      isError: updateSRStatusIsError,
      error: updateSRStatusError,
      isLoading: updateSRStatusIsLoading,
      isSuccess: updateSRStatusIsSuccess,
    },
  ] = useUpdateSRStatusMutation();

  // ---- Error Handeling -----

  useError(isError, error, "Get list routes for a SR failed");
  useError(isError, error, "Get all SR fail");
  useError(getAllAreaIsError, getAllAreaError, "Get all area fail");
  useError(getAllZoneIsError, getAllZoneError, "Get all zone fail");
  useError(
    getAllTerritoryIsError,
    getAllTerritoryError,
    "Get all territory fail"
  );
  useError(getAllRouteIsError, getAllRouteError, "Get all route fail");
  useError(createSRIsError, createSRError, "Create SR fail");
  useError(updateSRIsError, updateSRError, "Update SR fail");
  useError(getSingleSRIsError, getSingleSRError, "Get single SR data fail");
  useError(updateSRStatusIsError, updateSRStatusError, "Update SR status fail");

  // ---- Function Handeling ---------

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const showDrawerOfficer = () => {
    setOpenOfficer(true);
  };

  const onCloseOfficer = () => {
    setOpenOfficer(false);
  };

  const handleOkay = () => {
    handleCloseModal();
    handleSuccessClose();
    handleConfirmClose();
    handleConfirmStatusClose();
  };

  const handleCloseModal = () => {
    setSelectedId(null);
    setSelectedZoneId(null);
    setSelectedAreaId(null);
    setSelectedTerritoryId(null);
    form.setFieldsValue({});
    form.resetFields();
    onCloseOfficer();
    handleViewClose();
  };

  const onFinish = (values: any) => {
    const newValues = {
      ...values,
      userId: Number(userId),
      companyId: user?.data?.company_id,
    };
    handleConfirmOpen({ type: "CONFIRM", data: newValues });
  };

  const handleSendToApprove = () => {
    const newUpdateData = {
      ...openConfirmModal?.data,
      active: getSingleSRData?.data?.active,
    };

    if (openConfirmModal?.type === "CONFIRM" && selectedId) {
      updateSR({ data: newUpdateData, id: selectedId });
    } else {
      createSR(openConfirmModal?.data);
    }
  };

  const handleStatus = () => {
    const newData = {
      active: openConfirmStatusModal?.data?.active === true ? false : true,
    };
    updateSRStatus({ data: newData, id: openConfirmStatusModal?.data?.id });
  };

  const getModalTitle = () => {
    if (openOfficer && selectedId) {
      return "Route Successfully Updated!";
    }
    if (openOfficer) {
      return "New Route Successfully Added!";
    }
    if (
      openConfirmStatusModal?.type === "CONFIRMSTATUS" &&
      openConfirmStatusModal?.data?.active === false
    ) {
      return "Route Activated!";
    }
    if (
      openConfirmStatusModal?.type === "CONFIRMSTATUS" &&
      openConfirmStatusModal?.data?.active === true
    ) {
      return "Route Deactivated!";
    }
    return "";
  };

  useEffect(() => {
    if (getSingleSRData?.data && selectedId) {
      form.setFieldsValue({
        userId: getSingleSRData?.data?.userId,
        zoneId: getSingleSRData?.data?.zone?.id,
        areaId: getSingleSRData?.data?.area?.id,
        territoryId: getSingleSRData?.data?.territory?.id,
        routeId: getSingleSRData?.data?.route?.id,
      });
      setSelectedZoneId(getSingleSRData?.data?.zone?.id);
      setSelectedAreaId(getSingleSRData?.data?.area?.id);
      setSelectedTerritoryId(getSingleSRData?.data?.territory?.id);
    }
  }, [
    getSingleSRData?.data,
    selectedId,
    setSelectedZoneId,
    setSelectedAreaId,
    setSelectedTerritoryId,
    form,
  ]);

  useEffect(() => {
    if (createSRIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSRIsSuccess]);

  useEffect(() => {
    if (updateSRIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSRIsSuccess]);

  useEffect(() => {
    if (updateSRStatusIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSRStatusIsSuccess]);

  const detailsData = [
    {
      label: "Name",
      value: getSingleSRData?.data?.user?.username
        ? capitalize(getSingleSRData?.data?.user?.username)
        : "N/A",
    },
    {
      label: "Zone",
      value: getSingleSRData?.data?.zone?.zoneName
        ? capitalize(getSingleSRData?.data?.zone?.zoneName)
        : "N/A",
    },
    {
      label: "Area",
      value: getSingleSRData?.data?.area?.areaName
        ? capitalize(getSingleSRData?.data?.area?.areaName)
        : "N/A",
    },
    {
      label: "Territory",
      value: getSingleSRData?.data?.territory?.territoryName
        ? capitalize(getSingleSRData?.data?.territory?.territoryName)
        : "N/A",
    },
    {
      label: "Route",
      value: getSingleSRData?.data?.route?.routeName
        ? capitalize(getSingleSRData?.data?.route?.routeName)
        : "N/A",
    },
    {
      label: "Status",
      value: getSingleSRData?.data?.active === true ? "Active" : "Inactive",
    },
  ];

  const columns: ColumnsType<GetListRoutesSRDataType> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="text-nowrap">Zone</div>,
      dataIndex: "zone",
      render: (val) => {
        return val?.zoneName ? capitalize(val?.zoneName) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Area</div>,
      dataIndex: "area",
      render: (val) => {
        return val?.areaName ? capitalize(val?.areaName) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Territory</div>,
      dataIndex: "territory",
      render: (val) => {
        return val?.territoryName ? capitalize(val?.territoryName) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Route</div>,
      dataIndex: "route",
      render: (val) => {
        return val?.routeName ? capitalize(val?.routeName) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Collection (BDT)</div>,
      dataIndex: "totalCollectionAmount",
      align: "center",
      render: (val) => {
        return val ? formatNumber(val) : "0";
      },
    },
    {
      title: <div className="text-nowrap">Deposit (BDT)</div>,
      dataIndex: "totalDepositAmount",
      align: "center",
      render: (val) => {
        return val ? formatNumber(val) : "0";
      },
    },
    {
      title: <div className="text-nowrap">Remaining (BDT)</div>,
      dataIndex: "totalDueAmount",
      align: "center",
      render: (val) => {
        return val ? formatNumber(val) : "0";
      },
    },
    {
      title: (
        <div className="whitespace-nowrap w-full flex justify-center text-center">
          Status
        </div>
      ),
      dataIndex: "active",
      align: "center",
      render: (val: boolean) => {
        const status = val ? "Active" : "Inactive";
        const statusClasses: Record<string, string> = {
          Active: "bg-green-100 text-green-700",
          Inactive: "bg-red-100 text-red-700",
        };

        return (
          <div className="flex justify-center">
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${statusClasses[status]}`}
            >
              {status}
            </span>
          </div>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      align: "center",
      render: (val: number, record: any) => {
        const items = [
          {
            key: "active",
            label: (
              <div className="flex justify-between items-center pb-2 border-b border-[#D7DFE9]">
                <span className="text-gray-700">Active</span>
                <Switch
                  checked={record?.active === true}
                  size="small"
                  onClick={() => {
                    handleConfirmStatusOpen({
                      type: "CONFIRMSTATUS",
                      data: record,
                    });
                  }}
                />
              </div>
            ),
          },
          {
            key: "details",
            label: (
              <div
                className="flex items-center gap-2 justify-start text-slate-700 text-sm font-medium"
                onClick={() => {
                  setSelectedId(val);
                  handleViewOpen({ type: "VIEW" });
                }}
              >
                <EyeOutlined />
                <span>Details</span>
              </div>
            ),
          },
          {
            key: "edit",
            label: (
              <div
                className="flex items-center gap-2 justify-start text-slate-700 text-sm font-medium"
                onClick={() => {
                  setSelectedId(val);
                  showDrawerOfficer();
                }}
              >
                <EditIcon />
                <span>Edit</span>
              </div>
            ),
          },
        ];

        return (
          <div className="flex justify-center items-center gap-2">
            <Dropdown
              menu={{
                items,
                className: "min-w-[170px]",
              }}
              trigger={["click"]}
              placement="bottomRight"
              arrow={{ pointAtCenter: true }}
            >
              <Button
                className="action-button disabled:!bg-[#ddd] !border-none"
                size="small"
                icon={<ActionThreeDotIcon />}
              />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <SidebarLayout
        title={`${capitalize(userName || "")}`}
        backLink={backLink}
      >
        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.count}
          dataSource={addKeyInArray(data?.data?.rows)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Select
                placeholder="Status"
                showSearch
                options={[
                  { label: "Active", value: true },
                  {
                    label: "Inactive",
                    value: false,
                  },
                ]}
                value={filter?.active ?? undefined}
                onChange={(value) => handleFilter("active", value)}
                className="!min-w-[110px]"
                suffixIcon={<SelectDownIcon />}
                allowClear
                optionFilterProp="label"
              />

              <IconButton
                className="flex items-center text-[#7a8799]"
                icon={<FilterIconThree />}
                onClick={() => showDrawer()}
              >
                Filter
              </IconButton>

              <Button
                type="primary"
                className="middle-button"
                icon={<AddIcon />}
                onClick={() => showDrawerOfficer()}
              >
                Assign New Route
              </Button>
            </>
          }
        />
      </SidebarLayout>

      <Drawer open={open} width={400} onClose={onClose}>
        <div className="justify-start text-zinc-700 text-xl font-semibold">
          Filter
        </div>

        <div>
          {/* Zone */}
          <Select
            placeholder="Select Zone"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-8"
            options={
              getAllZoneData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.zoneName),
                value: val?.id,
              })) || []
            }
            value={selectedZoneId}
            onChange={(value) => {
              setSelectedZoneId(value ?? null);
              setSelectedAreaId(null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
            }}
          />

          {/* Area */}
          <Select
            placeholder="Select Area"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedZoneId}
            options={
              getAllAreaData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.areaName),
                value: val?.id,
              })) || []
            }
            value={selectedAreaId}
            onChange={(value) => {
              setSelectedAreaId(value ?? null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
            }}
          />

          {/* Territory */}
          <Select
            placeholder="Select Territory"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedAreaId}
            options={
              getAllTerritoryData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.territoryName),
                value: val?.id,
              })) || []
            }
            value={selectedTerritoryId}
            onChange={(value) => {
              setSelectedTerritoryId(value ?? null);
              setSelectedRouteId(null);
            }}
          />

          {/* Route */}
          <Select
            placeholder="Route"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedTerritoryId}
            options={
              getAllRouteData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.routeName),
                value: val?.id,
              })) || []
            }
            value={selectedRouteId}
            onChange={(value) => setSelectedRouteId(value ?? null)}
          />

          {/* Field Officer */}
          {/* <Select
                  placeholder="Field Officer"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="w-full mt-5"
                  options={
                    getAllUserData?.data?.users?.map((user: any) => ({
                      label: capitalize(user.username),
                      value: user.id,
                    })) || []
                  }
                  value={selectedUserId}
                  onChange={(value) => setSelectedUserId(value ?? null)}
                /> */}
        </div>

        <Space className="w-full mt-8" size={12}>
          {/* Cancel → clear everything */}
          <Button
            className="normal-dark-button modal-button !h-[40px]"
            onClick={() => {
              setSelectedZoneId(null);
              setSelectedAreaId(null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
              setSelectedUserId(null);
              handleFilter("zoneId", undefined);
              handleFilter("areaId", undefined);
              handleFilter("territoryId", undefined);
              handleFilter("routeId", undefined);
              // handleFilter("userId", undefined);
              onClose();
            }}
            size="middle"
          >
            Cancel
          </Button>

          {/* Apply Filter */}
          <Button
            className="modal-button !h-[40px]"
            type="primary"
            size="middle"
            onClick={() => {
              handleFilter("zoneId", selectedZoneId ?? undefined);
              handleFilter("areaId", selectedAreaId ?? undefined);
              handleFilter("territoryId", selectedTerritoryId ?? undefined);
              handleFilter("routeId", selectedRouteId ?? undefined);
              // handleFilter("userId", selectedUserId ?? undefined);
              onClose();
            }}
          >
            Filter
          </Button>
        </Space>
      </Drawer>

      <Drawer open={openOfficer} width={400} onClose={handleCloseModal}>
        <div className="justify-start text-zinc-700 text-xl font-semibold">
          {selectedId ? "Update Route" : "Assign New Route"}
        </div>

        {getAllZoneIsLoading ||
        getAllZoneIsFetching ||
        getAllAreaIsLoading ||
        getAllAreaIsFetching ||
        getAllTerritoryIsLoading ||
        getAllTerritoryIsFetching ||
        getAllRouteIsLoading ||
        getAllRouteIsFetching ||
        getSingleSRIsLoading ||
        getSingleSRIsFetching ? (
          <Loader />
        ) : (
          <div className="mt-5">
            <Form
              className="custom-form"
              layout="vertical"
              requiredMark="optional"
              size="large"
              onFinish={onFinish}
              form={form}
            >
              <Form.Item
                label="Zone"
                name="zoneId"
                rules={[
                  {
                    required: true,
                    message: "Please enter zone",
                  },
                ]}
              >
                <Select
                  placeholder="Select Zone"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="w-full"
                  options={
                    getAllZoneData?.data?.rows?.map((val: any) => ({
                      label: capitalize(val?.zoneName),
                      value: val?.id,
                    })) || []
                  }
                  onChange={(value) => {
                    setSelectedZoneId(value);
                    form.setFieldsValue({
                      areaId: undefined,
                      territoryId: undefined,
                      routeId: undefined,
                    });
                  }}
                  optionFilterProp="label"
                />
              </Form.Item>

              <Form.Item
                label="Area"
                name="areaId"
                rules={[
                  {
                    required: true,
                    message: "Please enter area",
                  },
                ]}
              >
                <Select
                  placeholder="Select Area"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="w-full"
                  disabled={!selectedZoneId}
                  options={
                    getAllAreaData?.data?.rows?.map((val: any) => ({
                      label: capitalize(val?.areaName),
                      value: val?.id,
                    })) || []
                  }
                  onChange={(value) => {
                    setSelectedAreaId(value);
                    form.setFieldsValue({
                      territoryId: undefined,
                      routeId: undefined,
                    });
                  }}
                  optionFilterProp="label"
                />
              </Form.Item>

              <Form.Item
                label="Teritory"
                name="territoryId"
                rules={[
                  {
                    required: true,
                    message: "Please enter teritory",
                  },
                ]}
              >
                <Select
                  placeholder="Select Territory"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="w-full"
                  disabled={!selectedAreaId}
                  options={
                    getAllTerritoryData?.data?.rows?.map((val: any) => ({
                      label: capitalize(val?.territoryName),
                      value: val?.id,
                    })) || []
                  }
                  onChange={(value) => {
                    setSelectedTerritoryId(value);
                    form.setFieldsValue({ routeId: undefined });
                  }}
                  optionFilterProp="label"
                />
              </Form.Item>

              <Form.Item
                label="Route"
                name="routeId"
                rules={[
                  {
                    required: true,
                    message: "Please enter route",
                  },
                ]}
              >
                <Select
                  placeholder="Select Route"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="w-full"
                  disabled={!selectedTerritoryId}
                  options={
                    getAllRouteData?.data?.rows?.map((val: any) => ({
                      label: capitalize(val?.routeName),
                      value: val?.id,
                    })) || []
                  }
                  optionFilterProp="label"
                />
              </Form.Item>

              <Space className="w-full mt-5" size={12}>
                <Button
                  className="normal-dark-button modal-button !h-[40px]"
                  onClick={handleCloseModal}
                  size="middle"
                >
                  Cancel
                </Button>

                <Button
                  className="modal-button !h-[40px]"
                  type="primary"
                  size="middle"
                  htmlType="submit"
                >
                  {selectedId ? "Update" : "Assign"}
                </Button>
              </Space>
            </Form>
          </div>
        )}
      </Drawer>

      {openViewModal?.type === "VIEW" && (
        <CashCollectionDetailsModal
          open={openViewModal?.open}
          handleClose={handleCloseModal}
        >
          <div>
            <div className="justify-start text-zinc-700 text-xl font-medium">
              Details
            </div>
            {getSingleSRIsLoading || getSingleSRIsFetching ? (
              <Loader />
            ) : (
              <div className="pr-4 mt-5">
                {detailsData.map((item, index) => (
                  <div
                    key={index}
                    className={`flex justify-between py-2 ${
                      index !== detailsData.length - 1
                        ? "border-b border-[#D7DFE9]"
                        : ""
                    }`}
                  >
                    <span className="text-slate-500 text-base font-normal">
                      {item.label}
                    </span>
                    <span
                      className={`text-base font-normal px-2 py-1 rounded ${
                        item.value === "Active"
                          ? "bg-green-100 text-green-700"
                          : item.value === "Inactive"
                          ? "bg-red-100 text-red-700"
                          : "text-zinc-700"
                      }`}
                    >
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CashCollectionDetailsModal>
      )}

      {openConfirmModal?.type === "CONFIRM" && (
        <ConfirmModalLayout
          title={`${selectedId ? "Update Route?" : "Assign New Route?"}`}
          subTitle={`${
            selectedId
              ? "Are you sure you want to Update Route?"
              : "Are you sure you want to assign new route?"
          }`}
          buttonName={`${selectedId ? "Update Route" : "Assign Route"}`}
          handleButton={handleSendToApprove}
          open={openConfirmModal?.open}
          handleClose={handleConfirmClose}
          action
          buttonBgDeep
          loading={createSRIsLoading || updateSRIsLoading}
        ></ConfirmModalLayout>
      )}

      {openConfirmStatusModal?.type === "CONFIRMSTATUS" && (
        <ConfirmModalLayout
          title={`${
            openConfirmStatusModal?.data?.active === true
              ? "Inactivate Route?"
              : "Activate Route?"
          }`}
          subTitle={`${
            openConfirmStatusModal?.data?.active === true
              ? "Are you sure you want to inactivate this route?"
              : "Are you sure you want to activate this route?"
          }`}
          buttonName={`${
            openConfirmStatusModal?.data?.active === true
              ? "Inactive"
              : "Active"
          }`}
          handleButton={handleStatus}
          open={openConfirmStatusModal?.open}
          handleClose={handleConfirmStatusClose}
          action
          buttonBgDeep
          loading={updateSRStatusIsLoading}
        ></ConfirmModalLayout>
      )}

      {openSuccessModal.type === "SUCCESS" && (
        <SuccessModal
          open={openSuccessModal?.open}
          handleClose={handleSuccessClose}
          handleOkay={handleOkay}
          data={openSuccessModal?.data}
          title={getModalTitle()}
        />
      )}
    </>
  );
}
